import React, { useState, useEffect, useMemo } from 'react';

// Iconos
import EditNoteIcon from '@mui/icons-material/EditNote';
import RememberMeIcon from '@mui/icons-material/RememberMe';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';


interface Columna {
  key: string;
  label: string;
  align: 'left' | 'center' | 'right';
}

interface TablaProps<T> {
  columns: Columna[];
  data: T[];
  onEdit: (item: T) => void; // Función que retorna el enlace de edición
  onCredencial: (item: T) => void; // Función que retorna el enlace de credencial
  itemsPerPage?: number;
  acciones: string[];
  onDelete: (item: T) => void;
  onUbication: (item: T) => void;
}

export function Tabla<T extends Record<string, any>>({
    columns,
    data,
    onEdit,
    onCredencial,
    itemsPerPage = 10,
    acciones,
    onDelete,
    onUbication
    }: TablaProps<T>) {

    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
  
    const filteredData = useMemo(() => {
        return data.filter(item =>
          columns.some(column => {
            if (!item[column.key]) return false; // Evitar errores si el valor es null o undefined
            return item[column.key].toString().toLowerCase().includes(searchTerm.toLowerCase());
          })
        );
      }, [data, searchTerm, columns]);
    
      const totalPages = Math.ceil(filteredData.length / itemsPerPage);
      
      useEffect(() => {
        setCurrentPage(1);
      }, [searchTerm]);
    
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const currentPageData = filteredData.slice(startIndex, endIndex);
    
    const nextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };
    
    const prevPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
      };
    
      const clearSearch = () => {
        setSearchTerm('');
      };

      const isFiltered = searchTerm !== '';

    return (
    <div>
      <div className="mb-4 flex space-x-2">
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={handleSearch}
          className="px-4 py-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex-grow"
        />
        <button
          onClick={clearSearch}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-r-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Limpiar
        </button>
      </div>        
    <div className="overflow-x-auto pe-2">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 text-${column.align} text-xs font-medium text-gray-800 uppercase tracking-wider`}
              >
                <strong>{column.label}</strong>
              </th>
            ))}
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                <strong>Acciones</strong>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {currentPageData.map((item, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-${column.align}`}
                >
                  {item[column.key]}
                </td>
              ))}
              <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium">
                {acciones.includes('editar') && (
                  <button className="text-vp-azul hover:text-vp-cian" title="Editar" onClick={() => onEdit(item)}>
                    <EditNoteIcon />
                  </button>
                )}
                {acciones.includes('eliminar') && (
                  <button className="text-red-800 hover:text-red-600" title="Eliminar" onClick={() => onDelete(item)}>
                    <DeleteIcon />
                  </button>
                )}
                {acciones.includes('ubicacion') && (
                  <button className="text-green-800 hover:text-green-600" title="Ubicación" onClick={() => onUbication(item)}>
                    <LocationOnIcon />
                  </button>
                )}

                {acciones.includes('credencial') && (
                  <button className="text-vp-azul hover:text-vp-cian" title="Credencial" onClick={() => onCredencial(item)}>
                    <RememberMeIcon />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Anterior
          </button>
          <button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            {!isFiltered && (
                <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{startIndex + 1}</span> a <span className="font-medium">{Math.min(endIndex, data.length)}</span> de{' '}
                    <span className="font-medium">{data.length}</span> resultados
                </p>
            )}           
            {(isFiltered && filteredData.length > 0) && (
                <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{startIndex + 1}</span> a <span className="font-medium">{Math.min(endIndex, filteredData.length)}</span> de{' '}
                    <span className="font-medium">{filteredData.length}</span> resultados (de un total de <span className="font-medium">{data.length}</span>)
                </p>
            )}
            {(isFiltered && filteredData.length === 0) && (
                <p className="text-sm text-gray-700">
                    No hay resultados para mostrar (de un total de <span className="font-medium">{data.length}</span>)
                </p>
            )}            
          </div>
          {totalPages > 1 && (
            <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                    Anterior
                </button>
                <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                    Siguiente
                </button>
                </nav>
            </div>)}
        </div>
      </div>
    </div>    
  );
}